/**
 * MessagesTab — messages thread + compose box.
 * Extracted from LeadDetailPage.tsx (iteration 4 refactor-split).
 *
 * Messages data comes from lead.messages (included in fetchLead response) — no new useQuery needed.
 * No lazy fetching needed for this tab.
 */

import { useState, useRef, useEffect } from 'react';
import { Paperclip, X } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { useQuery } from '@tanstack/react-query';
import { caseFeedbackQueryKey, listCaseFeedback, reportsByEventId } from '../../../api/agent-feedback.api';
import { MessageFeedbackControls } from '../../../components/agent/improvement/ImprovementEntryButton';
import { isImprovableAgentMessage } from '../../../components/agent/improvement/improvableMessage';
import { asIsoDateTimeString } from '@resida/shared';
import { usePropertyTimezone } from '../../../hooks/usePropertyTimezone';
import { formatTimestamp } from '../../../utils/date';
import { sendLeadReply, retryMessage } from '../../../lib/leasing.api';
import type { LeadMessage } from './shared';
import { formatAge } from './shared-constants';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface MessagesTabProps {
  leadId: string;
  messages: LeadMessage[];
  /** Who the lead is, for the improvement panel subtitle: "About a reply to <lead> · <property>". */
  leadName: string;
  propertyAddress: string | null;
  onMessageSent: () => void;
}

/** Authors whose messages carry the "That wasn't right" entry (IMP-BR-4, BR-AGENT-FB-1). */
function isAgentAuthoredMessage(msg: LeadMessage): boolean {
  return msg.caseEventId !== null && isImprovableAgentMessage('case.message_sent', msg.authorKind);
}

// ---------------------------------------------------------------------------
// MessagesThread (local)
// ---------------------------------------------------------------------------

function MessagesThread({ leadId, messages, leadName, propertyAddress, onMessageSent }: MessagesTabProps) {
  const [content, setContent] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const [attachedFiles, setAttachedFiles] = useState<File[]>([]);
  const [retryingIds, setRetryingIds] = useState<Set<string>>(new Set());
  const [retryErrors, setRetryErrors] = useState<Record<string, string>>({});
  const threadEndRef = useRef<HTMLDivElement>(null);
  const propertyTimezone = usePropertyTimezone();
  // A lead IS its case (Lead.id = Case.id), so the lead id is the feedback case id.
  const { data: feedbackData } = useQuery({
    queryKey: caseFeedbackQueryKey(leadId),
    queryFn: () => listCaseFeedback(leadId),
    staleTime: 30_000,
  });
  const reportsByEvent = reportsByEventId(feedbackData?.reports ?? []);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const leadSubtitle = `About a reply to ${leadName}${propertyAddress ? ` · ${propertyAddress}` : ''}`;

  useEffect(() => {
    threadEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages.length]);

  // Determine which inbound messages need a reply.
  // "Needs reply" = inbound message with routingDecision='created' AND status='pending'.
  // We capture this at load time (before markRead fires) so badges reflect the initial state.
  const needsReplyIds = new Set<string>();
  for (const msg of messages) {
    if (
      msg.direction === 'inbound' &&
      msg.routingDecision === 'created' &&
      msg.status === 'pending'
    ) {
      needsReplyIds.add(msg.id);
    }
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    setAttachedFiles((prev) => {
      const existingNames = new Set(prev.map((f) => f.name));
      return [...prev, ...files.filter((f) => !existingNames.has(f.name))];
    });
    // Reset file input so the same file can be re-attached after removal
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeFile = (fileName: string) => {
    setAttachedFiles((prev) => prev.filter((f) => f.name !== fileName));
  };

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!content.trim() && attachedFiles.length === 0) return;
    setSendError(null);
    setIsSending(true);
    try {
      await sendLeadReply(leadId, content.trim(), attachedFiles.length > 0 ? attachedFiles : undefined);
      setContent('');
      setAttachedFiles([]);
      onMessageSent();
    } catch (err) {
      setSendError(err instanceof Error ? err.message : 'Failed to send reply');
    } finally {
      setIsSending(false);
    }
  };

  const handleRetry = async (channelMessageId: string) => {
    setRetryErrors((prev) => {
      if (!(channelMessageId in prev)) return prev;
      const next = { ...prev };
      delete next[channelMessageId];
      return next;
    });
    setRetryingIds((prev) => new Set(prev).add(channelMessageId));
    try {
      await retryMessage(channelMessageId);
      onMessageSent();
    } catch (err) {
      setRetryErrors((prev) => ({
        ...prev,
        [channelMessageId]: err instanceof Error ? err.message : 'Failed to retry message',
      }));
    } finally {
      setRetryingIds((prev) => {
        const next = new Set(prev);
        next.delete(channelMessageId);
        return next;
      });
    }
  };

  return (
    <section data-testid="lead-messages-thread" className="mb-6 rounded-lg border border-border bg-card p-5">
      <h2 className="text-base font-semibold mb-4">Messages</h2>

      {/* Thread list */}
      <div className="space-y-3 max-h-96 overflow-y-auto mb-4 pr-1">
        {messages.length === 0 ? (
          <p className="text-sm text-muted-foreground italic">No messages yet.</p>
        ) : (
          messages.map((msg) => {
            const isOutbound = msg.direction === 'outbound';
            const needsReply = needsReplyIds.has(msg.id);
            const isFailed = isOutbound && msg.status === 'failed';
            const isRetrying = retryingIds.has(msg.id);
            const retryError = retryErrors[msg.id];
            return (
              <div
                key={msg.id}
                id={`msg-${msg.id}`}
                data-testid={`message-${msg.id}`}
                className={`flex flex-col ${isOutbound ? 'items-end' : 'items-start'}`}
              >
                <div
                  className={`max-w-[80%] rounded-lg px-4 py-2.5 text-sm ${
                    isOutbound
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted text-foreground'
                  }`}
                >
                  <p className="whitespace-pre-wrap">{msg.content}</p>
                </div>
                {isAgentAuthoredMessage(msg) && (
                  <div className="mt-1 flex flex-wrap items-center gap-2">
                    <MessageFeedbackControls
                      eventId={msg.caseEventId as string}
                      report={reportsByEvent.get(msg.caseEventId as string)}
                      request={{
                        start: {
                          entry: 'message',
                          anchor: { kind: 'message', caseEventId: msg.caseEventId as string },
                          caseId: leadId,
                        },
                        agentKind: 'leasing',
                        subtitle: leadSubtitle,
                        anchorCard: {
                          label: 'The agent sent',
                          text: msg.content,
                          at: formatTimestamp(asIsoDateTimeString(msg.createdAt), propertyTimezone),
                        },
                      }}
                    />
                  </div>
                )}
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-xs text-muted-foreground">
                    {msg.authorKind === 'agent' ? 'Agent' : msg.authorKind === 'router' ? 'Router' : isOutbound ? 'You' : 'Prospect'} · {formatAge(msg.createdAt)}
                  </span>
                  {needsReply && (
                    <span
                      data-testid={`message-needs-reply-${msg.id}`}
                      className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-destructive/10 text-destructive border border-destructive/30"
                    >
                      Needs Reply
                    </span>
                  )}
                  {isFailed && (
                    <>
                      <span
                        data-testid={`message-failed-${msg.id}`}
                        className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-destructive/10 text-destructive border border-destructive/30"
                      >
                        Failed to send
                      </span>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        data-testid={`retry-message-${msg.id}`}
                        aria-label="Retry sending message"
                        onClick={() => void handleRetry(msg.id)}
                        disabled={isRetrying}
                      >
                        {isRetrying ? 'Retrying…' : 'Retry'}
                      </Button>
                    </>
                  )}
                </div>
                {isFailed && retryError && (
                  <div
                    data-testid={`retry-error-${msg.id}`}
                    className="p-2 bg-destructive/10 border border-destructive/30 rounded text-destructive text-xs mt-1 max-w-[80%]"
                    role="alert"
                  >
                    {retryError}
                  </div>
                )}
              </div>
            );
          })
        )}
        <div ref={threadEndRef} />
      </div>

      {/* Compose box */}
      <form onSubmit={(e) => void handleSend(e)} className="space-y-2">
        <textarea
          data-testid="lead-compose-box"
          value={content}
          onChange={(e) => setContent(e.target.value)}
          rows={3}
          placeholder="Type a reply…"
          aria-label="Compose reply message"
          className="w-full border border-input rounded-md px-3 py-2 text-sm bg-background text-foreground outline-none focus:ring-2 focus:ring-ring resize-y"
          disabled={isSending}
        />

        {/* Attachment chips */}
        {attachedFiles.length > 0 && (
          <div className="flex flex-wrap gap-2" data-testid="attachment-chips">
            {attachedFiles.map((file) => (
              <span
                key={file.name}
                data-testid={`attachment-chip-${file.name}`}
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-muted text-foreground border border-border"
              >
                <Paperclip className="h-3 w-3 text-muted-foreground" aria-hidden="true" />
                {file.name}
                <button
                  type="button"
                  onClick={() => removeFile(file.name)}
                  aria-label={`Remove attachment ${file.name}`}
                  className="ml-0.5 rounded-full hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-colors p-0.5"
                  disabled={isSending}
                >
                  <X className="h-3 w-3" aria-hidden="true" />
                </button>
              </span>
            ))}
          </div>
        )}

        {sendError && (
          <div
            data-testid="lead-send-error"
            className="p-2 bg-destructive/10 border border-destructive/30 rounded text-destructive text-xs"
            role="alert"
          >
            {sendError}
          </div>
        )}

        <div className="flex items-center justify-between gap-2">
          {/* Hidden file input */}
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept="image/*,.pdf,.doc,.docx"
            className="sr-only"
            id="lead-file-input"
            onChange={handleFileChange}
            disabled={isSending}
            aria-label="Attach files"
          />
          {/* Attach button */}
          <Button
            type="button"
            variant="outline"
            size="sm"
            data-testid="attach-file-button"
            aria-label="Attach files to reply"
            onClick={() => fileInputRef.current?.click()}
            disabled={isSending}
            className="flex items-center gap-1.5"
          >
            <Paperclip className="h-3.5 w-3.5" aria-hidden="true" />
            Attach
          </Button>

          <Button
            type="submit"
            data-testid="send-reply-button"
            disabled={isSending || (!content.trim() && attachedFiles.length === 0)}
            aria-label="Send reply"
          >
            {isSending ? 'Sending…' : 'Send Reply'}
          </Button>
        </div>
      </form>
    </section>
  );
}

// ---------------------------------------------------------------------------
// MessagesTab
// ---------------------------------------------------------------------------

export function MessagesTab(props: MessagesTabProps) {
  return (
    <div>
      <MessagesThread {...props} />
    </div>
  );
}
